/**
 * Burgid - Flash promo bar (countdown + copyable discount codes).
 *
 * Horizon theme block. Runs the countdown, copies discount codes to the clipboard, and
 * keeps the "only N left" row in step with the selected variant.
 *
 * The timer deliberately mirrors blocks/burgid-flash-bar.liquid (the "⚡ Flash Sale"
 * bar): evergreen or fixed, one store-wide shared start time, modulo restart. Change
 * one and change the other, or the two bars drift apart.
 *
 * No theme imports on purpose. The countdown is the part shoppers see, so the module
 * must not be able to die because an import map entry moved.
 */

const SECOND = 1000;

/** @param {number} n */
function pad(n) {
  return String(n).padStart(2, '0');
}

/**
 * Split whole seconds into days/hours/minutes/seconds.
 * @param {number} totalSeconds
 */
function breakdown(totalSeconds) {
  const t = Math.max(0, Math.floor(totalSeconds));
  return {
    days: Math.floor(t / 86400),
    hours: Math.floor((t % 86400) / 3600),
    minutes: Math.floor((t % 3600) / 60),
    seconds: t % 60,
  };
}

class BurgidFlashPromo extends HTMLElement {
  /** @type {any} */
  #config = null;
  /** @type {HTMLElement | null} */
  #panel = null;
  /** @type {HTMLElement | null} */
  #ended = null;
  /** @type {HTMLFormElement | null} */
  #form = null;
  /** @type {number | null} */
  #timer = null;
  /** @type {AbortController | null} */
  #abort = null;

  /** Difference between the server's clock and this browser's, in ms. */
  #skewMs = 0;
  #expired = false;
  #initAttempts = 0;
  /** @type {Map<HTMLElement, number>} */
  #copyTimers = new Map();

  connectedCallback() {
    if (this.#config) return;

    const configEl = this.querySelector('[data-fp-config]');
    if (!configEl) {
      // The theme editor can attach the element before its children are in place.
      if (this.#initAttempts++ < 5) setTimeout(() => this.connectedCallback(), 0);
      return;
    }

    try {
      this.#config = JSON.parse(configEl.textContent || '{}');
    } catch (error) {
      return;
    }

    this.#panel = this.querySelector('[data-fp-panel]');
    this.#ended = this.querySelector('[data-fp-ended]');
    this.#form = this.closest('form[action*="/cart/add"]') || this.closest('form');

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    // Trust the server's clock over the device's. A phone set three days fast would
    // otherwise expire every sale it ever sees.
    if (Number(this.#config.serverNow) > 0) {
      this.#skewMs = Number(this.#config.serverNow) * SECOND - Date.now();
    }

    this.addEventListener('click', this.#onClick, { signal });

    // Variant switches bubble change/input inside the product form; the stock row
    // follows the selected variant, so re-read it whenever the form changes.
    if (this.#form) {
      this.#form.addEventListener('change', this.#syncStock, { signal });
      this.#form.addEventListener('input', this.#syncStock, { signal });
    }

    // A background tab throttles timers to once a minute or stops them outright, so
    // the digits are stale on return. Re-render the moment the tab is looked at again.
    document.addEventListener('visibilitychange', this.#onVisible, { signal });

    this.#syncStock();
    this.#start();
  }

  disconnectedCallback() {
    this.#stop();
    this.#abort?.abort();
    this.#abort = null;
    this.#copyTimers.forEach((id) => clearTimeout(id));
    this.#copyTimers.clear();
  }

  /* ---- deadline ----------------------------------------------------------- */

  /**
   * The evergreen start time, shared with the "⚡ Flash Sale" bar.
   *
   * ONE key for the whole store, exactly as blocks/burgid-flash-bar.liquid uses it:
   * every page that renders either block reads the same start, so the countdowns
   * agree wherever the shopper is.
   */
  #startTime() {
    const key = 'burgid_flash_sale_start';
    const store = this.#store();
    let t = null;

    try {
      t = parseInt(store.getItem(key), 10);
    } catch (error) {
      t = null;
    }

    if (!t || Number.isNaN(t)) {
      t = Date.now();
      try {
        store.setItem(key, String(t));
      } catch (error) {
        // Private mode, or storage blocked. The timer still runs for this pageview,
        // it just restarts on the next one.
      }
    }

    return t;
  }

  #store() {
    try {
      return this.#config.persistence === 'persistent' ? localStorage : sessionStorage;
    } catch (error) {
      // Storage constructors themselves throw when site data is blocked outright.
      return { getItem: () => null, setItem: () => {} };
    }
  }

  /**
   * Seconds left, or null when fixed mode has no end date to count to.
   * @returns {number | null}
   */
  #remaining() {
    const config = this.#config;

    if (config.timerMode === 'fixed') {
      const endMs = Number(config.endTs) * SECOND;
      if (!endMs) return null;
      // Only fixed mode gets the skew correction: it compares a server-derived
      // instant against the device clock. Evergreen is measured entirely against a
      // timestamp this browser wrote itself, so correcting it would skew both ends.
      return Math.floor((endMs - this.#now()) / SECOND);
    }

    const duration = Number(config.durationSeconds) || 900;
    const elapsed = Math.floor((Date.now() - this.#startTime()) / SECOND);

    if (config.onExpiry === 'restart') {
      // Modulo keeps every page in step even after it has looped.
      const m = elapsed % duration;
      return m === 0 ? duration : duration - m;
    }

    return duration - elapsed;
  }

  /** Browser clock corrected by the server offset. */
  #now() {
    return Date.now() + this.#skewMs;
  }

  /* ---- ticking ------------------------------------------------------------ */

  #start() {
    this.#tick();
    if (this.#expired) return;
    this.#timer = setInterval(() => this.#tick(), SECOND);
  }

  #stop() {
    if (this.#timer !== null) clearInterval(this.#timer);
    this.#timer = null;
  }

  #onVisible = () => {
    if (document.visibilityState === 'visible') this.#tick();
  };

  #tick() {
    const remaining = this.#remaining();

    // Fixed mode with no end date set: nothing to count to.
    if (remaining === null) {
      this.#paint(0);
      return;
    }

    if (remaining <= 0) {
      this.#paint(0);
      this.#expire();
      return;
    }

    this.#paint(remaining);
  }

  /** @param {number} remaining seconds */
  #paint(remaining) {
    const { days, hours, minutes, seconds } = breakdown(remaining);

    const dayUnit = this.querySelector('[data-fp-unit-d]');
    const daySep = this.querySelector('[data-fp-sep-d]');
    if (dayUnit) dayUnit.hidden = days === 0;
    if (daySep) daySep.hidden = days === 0;

    this.#setText('[data-fp-d]', pad(days));
    this.#setText('[data-fp-h]', pad(hours));
    this.#setText('[data-fp-m]', pad(minutes));
    this.#setText('[data-fp-s]', pad(seconds));

    // Screen readers get a coarse reading. Announcing every second would make the
    // page unusable, so this is only refreshed as the minute rolls over.
    if (seconds === 0 || !this.querySelector('[data-fp-sr]')?.textContent) {
      const parts = [];
      if (days) parts.push(`${days} days`);
      if (days || hours) parts.push(`${hours} hours`);
      parts.push(`${minutes} minutes`);
      this.#setText('[data-fp-sr]', `Offer ends in ${parts.join(', ')}`);
    }
  }

  #expire() {
    if (this.#expired) return;

    const onExpiry = this.#config.onExpiry;

    // `restart` never expires - #remaining() has already wrapped it round. Reaching
    // zero here just means the modulo landed exactly on the boundary.
    if (onExpiry === 'restart') return;

    this.#expired = true;
    this.#stop();

    if (onExpiry === 'hide') {
      this.hidden = true;
    } else if (onExpiry === 'message' && this.#ended) {
      this.#panel.hidden = true;
      this.#ended.hidden = false;
    }
    // `freeze` (and `message` with no message set) leaves the bar sitting at 00:00:00.
  }

  /* ---- copy --------------------------------------------------------------- */

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('[data-fp-copy]');
    if (!(button instanceof HTMLElement)) return;

    // The block sits inside the product form. The button is type="button" already,
    // but stop the click reaching any submit handler watching the form.
    event.preventDefault();

    const code = button.getAttribute('data-fp-copy') || '';
    this.#copy(code)
      .then(() => this.#flash(button, this.#config.copiedLabel || 'Copied!'))
      .catch(() => this.#flash(button, this.#config.copyFailLabel || 'Press Ctrl+C to copy'));
  };

  /** @param {string} text */
  async #copy(text) {
    if (navigator.clipboard?.writeText) {
      try {
        // A permission-gated or embedded webview can leave this promise pending
        // forever instead of rejecting, which would leave the shopper looking at an
        // unchanged button with no idea whether the code copied. Cap the wait and
        // fall through to the legacy path, which is synchronous and always settles.
        await Promise.race([
          navigator.clipboard.writeText(text),
          new Promise((_, reject) => {
            setTimeout(() => reject(new Error('clipboard timed out')), 1200);
          }),
        ]);
        return;
      } catch (error) {
        // Fall through and try the legacy path.
      }
    }

    // Safari below 13.4, and any non-secure origin, have no clipboard API.
    const scratch = document.createElement('textarea');
    scratch.value = text;
    scratch.setAttribute('readonly', '');
    scratch.style.position = 'fixed';
    scratch.style.opacity = '0';
    document.body.appendChild(scratch);
    scratch.select();

    let ok = false;
    try {
      ok = document.execCommand('copy');
    } catch (error) {
      ok = false;
    }
    scratch.remove();
    if (!ok) throw new Error('copy failed');
  }

  /**
   * Swap the code for a confirmation, then put it back.
   * @param {HTMLElement} button
   * @param {string} message
   */
  #flash(button, message) {
    const label = button.querySelector('[data-fp-code-text]');
    if (!label) return;

    const existing = this.#copyTimers.get(button);
    if (existing) clearTimeout(existing);
    else button.dataset.fpOriginal = label.textContent || '';

    label.textContent = message;
    button.classList.add('is-copied');

    const id = setTimeout(() => {
      label.textContent = button.dataset.fpOriginal || '';
      button.classList.remove('is-copied');
      this.#copyTimers.delete(button);
    }, 1800);

    this.#copyTimers.set(button, id);
  }

  /* ---- stock -------------------------------------------------------------- */

  #syncStock = () => {
    const row = this.querySelector('[data-fp-stock]');
    const text = this.querySelector('[data-fp-stock-text]');
    const meter = this.querySelector('[data-fp-meter]');
    const stock = this.#config?.stock;
    if (!(row instanceof HTMLElement) || !text || !(meter instanceof HTMLElement) || !stock) return;

    if (stock.source === 'off') {
      row.hidden = true;
      return;
    }

    let left;
    if (stock.source === 'manual') {
      left = Number(stock.manual);
    } else {
      const input = this.#form?.querySelector('input[name="id"]');
      const id = input instanceof HTMLInputElement ? String(input.value) : '';
      const level = id ? stock.levels?.[id] : undefined;

      // null means the variant does not track inventory, so there is no honest
      // number to show. undefined means the form has not told us the variant yet -
      // leave whatever the server rendered rather than flickering.
      if (level === undefined) return;
      if (level === null || Number(level) <= 0 || Number(level) > Number(stock.threshold)) {
        row.hidden = true;
        return;
      }
      left = Number(level);
    }

    const max = Number(stock.max) || 1;
    const pct = Math.min(92, Math.max(8, Math.round(((max - Math.min(left, max)) / max) * 100)));

    text.textContent = String(stock.template || '').replace('[n]', String(left));
    meter.style.width = `${pct}%`;
    row.hidden = false;
  };

  /**
   * @param {string} selector
   * @param {string} value
   */
  #setText(selector, value) {
    const el = this.querySelector(selector);
    if (el && el.textContent !== value) el.textContent = value;
  }
}

if (!customElements.get('burgid-flash-promo')) {
  customElements.define('burgid-flash-promo', BurgidFlashPromo);
}
